////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Light/dark theme toggle — reads/writes ConfigProvider's theme   //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  1.0.3 POST-REVIEW TWEAK (11/08/2026, tweak #2): the toggle's label ("Dark
//  mode"/"Light mode") reveals next to the icon on hover/focus rather than
//  living permanently on the control. Mike's exact concern was the menu's
//  sibling items shuffling position as the label grows in — solved by taking
//  the label fully OUT of flow (`absolute`, anchored to the icon button's own
//  box below) rather than animating a `max-width`/`width` on an in-flow
//  element: the wrapper `<span>`'s own geometry never changes, so nothing
//  around it ever moves, regardless of how long the revealed label text is.
//
////////////////////////////////////////////////////////////////////////////////

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';

import { useConfig } from '@/config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { labelReveal, reduceVariants } from '@/lib/motion';

/** A single control button showing the theme it will switch TO (Moon while light,
 * Sun while dark) — the same convention most OS/app theme toggles use. */
export function ThemeToggle () {
  const { cfg, setTheme } = useConfig();
  const reducedMotion = useReducedMotion();
  const [revealed, setRevealed] = useState(false);
  const isDark = cfg.client.theme === 'dark';
  const label = isDark ? 'Light mode' : 'Dark mode';
  const labelVariants = reduceVariants(labelReveal, reducedMotion);

  return (
    // `relative` — the ONLY box whose size matters for layout purposes. The
    // revealed label below is positioned against THIS box, not in normal flow,
    // so its width never contributes to this span's (or its parent <li>'s) size.
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={() => setRevealed(false)}
    >
      <button
        type="button"
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        // Permanent regardless of the visual reveal above — the reveal is sugar
        // for sighted pointer/keyboard users who are already looking at the
        // control; screen-reader users get the same name every time.
        aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
        className="rounded p-2 text-ink/70 outline-none transition-colors hover:text-ink focus:ring-2 focus:ring-ink/40"
      >
        {isDark ? <Sun aria-hidden="true" size={18} /> : <Moon aria-hidden="true" size={18} />}
      </button>

      {/* `aria-hidden` — decorative sugar duplicating the button's own
          aria-label; a screen reader must not announce it a second time. */}
      <motion.span
        aria-hidden="true"
        className="pointer-events-none absolute left-full ml-2 whitespace-nowrap text-sm text-ink/80"
        initial="hidden"
        animate={revealed ? 'visible' : 'hidden'}
        variants={labelVariants}
      >
        {label}
      </motion.span>
    </span>
  );
}
